import React, { useState, useMemo } from "react";

/**
 * A reusable searchable checkbox list component with filtering capability.
 * Useful for multi-select lists like stores.
 */
const SearchableCheckboxList = ({
  options = [],
  selectedOptions = [],
  onSelectionChange,
  displayFormatter,
  label,
  className = "",
  posthogId,
  posthogAttribute,
  maxHeight = "30vh",
}) => {
  const [searchTerm, setSearchTerm] = useState("");

  const getDisplayValue = (optionValue) => {
    if (displayFormatter) {
      return displayFormatter(optionValue);
    }
    return optionValue;
  };

  const sortedOptions = useMemo(() => {
    return [...options].sort((a, b) => 
      getDisplayValue(a).localeCompare(getDisplayValue(b))
    );
  }, [options, displayFormatter]);

  const filteredOptions = useMemo(() => {
    if (!searchTerm) return sortedOptions;
    const lowerSearchTerm = searchTerm.toLowerCase();
    return sortedOptions.filter((option) => {
      const displayValue = getDisplayValue(option);
      return displayValue.toLowerCase().includes(lowerSearchTerm);
    });
  }, [sortedOptions, searchTerm]);

  const handleToggle = (option) => {
    if (selectedOptions.includes(option)) {
      onSelectionChange(selectedOptions.filter((o) => o !== option));
    } else {
      onSelectionChange([...selectedOptions, option]);
    }
  };

  const handleSelectAll = () => {
    onSelectionChange(options);
  };

  const handleDeselectAll = () => {
    onSelectionChange([]);
  };

  const handleSelectFiltered = () => {
    const newSelection = [...new Set([...selectedOptions, ...filteredOptions])];
    onSelectionChange(newSelection);
  };

  const handleDeselectFiltered = () => {
    const filteredSet = new Set(filteredOptions);
    onSelectionChange(selectedOptions.filter((o) => !filteredSet.has(o)));
  };

  return (
    <div className={className}>
      {label && (
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {label}
        </label>
      )}
      
      <div className="mb-2">
        <input
          type="text"
          className="w-full p-2 border border-gray-300 rounded"
          placeholder="Search..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          data-posthog={`${posthogId}-search`}
        />
      </div>

      <div 
        className="flex flex-row flex-wrap justify-evenly p-2 gap-2 overflow-y-auto"
        style={{ maxHeight }}
      >
        {filteredOptions.map((option) => (
          <div 
            className="bg-gray-100 p-2 rounded-lg shadow basis-0 grow" 
            key={option}
          >
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                className="form-checkbox"
                checked={selectedOptions.includes(option)}
                onChange={() => handleToggle(option)}
                data-posthog={posthogId}
                {...(posthogAttribute ? { [posthogAttribute]: option } : {})}
              />
              {getDisplayValue(option)}
            </label>
          </div>
        ))}
        {filteredOptions.length === 0 && searchTerm && (
          <div className="w-full text-center text-gray-500 italic p-2">
            No matches found
          </div>
        )}
      </div>

      <div className="flex justify-between gap-2 mt-2">
        {searchTerm ? (
          <>
            <button
              className="flex-1 bg-blue-100 p-2 rounded-lg shadow"
              onClick={handleSelectFiltered}
              data-posthog={`${posthogId}-select-filtered`}
            >
              Select Filtered
            </button>
            <button
              className="flex-1 bg-blue-100 p-2 rounded-lg shadow"
              onClick={handleDeselectFiltered}
              data-posthog={`${posthogId}-deselect-filtered`}
            >
              Deselect Filtered
            </button>
          </>
        ) : (
          <>
            <button
              className="flex-1 bg-blue-100 p-2 rounded-lg shadow"
              onClick={handleSelectAll}
              data-posthog={`${posthogId}-select-all`}
            >
              Select All
            </button>
            <button
              className="flex-1 bg-blue-100 p-2 rounded-lg shadow"
              onClick={handleDeselectAll}
              data-posthog={`${posthogId}-deselect-all`}
            >
              Deselect All
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default SearchableCheckboxList;
